import React from "react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { X, Filter } from "lucide-react";
import { STATUS_BUCKETS } from "@/lib/workflow-filters";

/**
 * Reusable Open / Closed / Partially-closed + date-range filter bar for
 * Quote · SO · PO · PR · GRN registers. Iter-92 · Phase 1.
 */
export default function WorkflowFilters({
  value = { status: "all", from: "", to: "", party: "" },
  onChange,
  parties = null,     // Optional list [{id,name}] to enable party dropdown
  counts = null,      // Optional {all,open,partial,closed,cancelled} pill counts
  testIdPrefix = "wf-filter",
}) {
  const set = (patch) => onChange({ ...value, ...patch });
  const active = value.status !== "all" || value.from || value.to || value.party;

  return (
    <div className="card-sharp p-3 flex flex-wrap items-end gap-2" data-testid={`${testIdPrefix}-bar`}>
      <div className="flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest text-muted-foreground pr-1">
        <Filter className="w-3.5 h-3.5" /> Filters
      </div>

      {/* Status */}
      <div className="flex flex-col gap-1">
        <label className="text-[9px] font-mono uppercase text-muted-foreground">Status</label>
        <Select value={value.status} onValueChange={(v) => set({ status: v })}>
          <SelectTrigger className="rounded-sm h-8 w-[180px] text-xs" data-testid={`${testIdPrefix}-status`}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {STATUS_BUCKETS.map((b) => (
              <SelectItem key={b.key} value={b.key} data-testid={`${testIdPrefix}-status-${b.key}`}>
                {b.label}{counts && b.key !== "all" ? `  (${counts[b.key] || 0})` : counts && b.key === "all" ? `  (${counts.all || 0})` : ""}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Date from */}
      <div className="flex flex-col gap-1">
        <label className="text-[9px] font-mono uppercase text-muted-foreground">From</label>
        <Input type="date" value={value.from || ""} onChange={(e) => set({ from: e.target.value })}
                 className="rounded-sm h-8 w-[150px] text-xs" data-testid={`${testIdPrefix}-from`} />
      </div>

      {/* Date to */}
      <div className="flex flex-col gap-1">
        <label className="text-[9px] font-mono uppercase text-muted-foreground">To</label>
        <Input type="date" value={value.to || ""} onChange={(e) => set({ to: e.target.value })}
                 className="rounded-sm h-8 w-[150px] text-xs" data-testid={`${testIdPrefix}-to`} />
      </div>

      {/* Party (optional) */}
      {parties && (
        <div className="flex flex-col gap-1">
          <label className="text-[9px] font-mono uppercase text-muted-foreground">Party</label>
          <Select value={value.party || "all"} onValueChange={(v) => set({ party: v === "all" ? "" : v })}>
            <SelectTrigger className="rounded-sm h-8 w-[220px] text-xs" data-testid={`${testIdPrefix}-party`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="max-h-72">
              <SelectItem value="all">All parties</SelectItem>
              {parties.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      )}

      {active && (
        <Button variant="ghost" size="sm" className="rounded-sm h-8 gap-1 text-xs"
                  onClick={() => onChange({ status: "all", from: "", to: "", party: "" })}
                  data-testid={`${testIdPrefix}-clear`}>
          <X className="w-3 h-3" /> Clear
        </Button>
      )}
    </div>
  );
}
